import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { withApplicationContext } from '../../../../../utils/ApplicationContext';
import { noop } from '../../../../../utils/noop';
import { withProduct } from '../../../../../utils/ProductContext';
import AmbientImage from '../../../../ShopTheProject/AmbientImage';
import Image from '../../../../ui/Image/Image';
import { getMediaData } from '../../../ImageGalleryHelper';
import { useImageGalleryHeadline } from '../../hooks/useImageGalleryHeadline';
import ImageGalleryControlButtons from '../../ImageGalleryControlButtons/v2';
import YoutubeVideo from '../../ImageGalleryVideo/components/YoutubeVideo/YoutubeVideo';
import ImageGalleryVideo from '../../ImageGalleryVideo/ImageGalleryVideo';
import { ImageGalleryHeadlineDesktopStyles, PreloadImageStyles } from './style';

const ImageGalleryHeadlineDesktop = ({
  zoomSize,
  activeImageIndex,
  images,
  imagesPerSlide,
  activeIndicatorIndex,
  setActiveIndicatorIndex,
  setActiveImageIndex,
  imageQuality,
  headlineImageHeight,
  headlineImageWidth,
  showControlButtons,
  crop,
  setVideoPlayingForSoftline,
  isConnect,
  productName,
  brandName,
  product,
  appCtx,
}) => {
  const hookData = useImageGalleryHeadline({
    zoomSize,
    activeImageIndex,
    images,
    imagesPerSlide,
    activeIndicatorIndex,
    setActiveIndicatorIndex,
    setActiveImageIndex,
    imageQuality,
    headlineImageHeight,
    headlineImageWidth,
    crop,
    setVideoPlayingForSoftline,
    productName,
    brandName,
    product,
    appCtx,
  });

  const {
    zoomLevel,
    isVideoPlaying,
    isCoachmarkVisible,
    headlineRef,
    store,
    activeImageId,
    activeImageUrl,
    imageCrop,
    activeImgZoomBaseSrc,
    handleMouseLeave,
    handleMouseOver,
    handleMouseOverButton,
    onNextHeadline,
    onPreviousHeadLine,
    setIsCoachmarkVisible,
    shouldShowZoom,
    isVideoContent,
    isYoutubeVideo,
    isAmbientImage,
    altText,
    setVideoPlaying,
  } = hookData;

  const preloadImages = (cropSettings) => {
    return (
      <>
        {images.map((img, index) => {
          const { mediaId, mediaUrl } = getMediaData(img);
          const imagekey = `${mediaId}-desktop-${index}`;

          return (
            <div className="image-gallery-headline__preload" key={imagekey}>
              <Image
                id={mediaId}
                imageUrl={mediaUrl}
                width={headlineImageWidth}
                height={headlineImageHeight}
                alt={altText}
                useSrcSet={false}
                crop={cropSettings}
                inlineDimensions={false}
              />
            </div>
          );
        })}
        <style jsx>{PreloadImageStyles}</style>
      </>
    );
  };

  // Determine which component to show based on content type
  const renderMediaComponent = () => {
    if (isVideoContent) {
      if (isYoutubeVideo) {
        return (
          <YoutubeVideo
            url={activeImageUrl}
            setVideoPlaying={showControlButtons ? setVideoPlaying : setVideoPlayingForSoftline}
            store={store}
          />
        );
      }
      return (
        <ImageGalleryVideo
          url={activeImageUrl}
          setVideoPlaying={showControlButtons ? setVideoPlaying : setVideoPlayingForSoftline}
        />
      );
    }
    if (isAmbientImage) {
      return (
        <AmbientImage
          activeImageIndex={activeImageIndex + 1}
          mainProductImage={activeImageUrl}
          setIsCoachmarkVisible={setIsCoachmarkVisible}
        />
      );
    }
    return (
      <Image
        id={activeImageId}
        imageUrl={activeImageUrl}
        type="headline"
        width={headlineImageWidth}
        height={headlineImageHeight}
        alt={altText}
        useSrcSet={false}
        inlineDimensions={false}
      />
    );
  };

  return (
    <div
      className={cx(
        'image-gallery-headline',
        isAmbientImage && isCoachmarkVisible && 'image-gallery-headline--overflow-visible'
      )}
      onMouseMove={(e) => {
        handleMouseOver(e);
      }}
      onMouseLeave={(e) => {
        handleMouseLeave(e);
      }}
      onFocus={noop}
      ref={(r) => {
        headlineRef.current = r;
      }}
      style={{
        overflow: isAmbientImage && isCoachmarkVisible ? 'visible' : 'hidden',
      }}
    >
      {preloadImages(imageCrop)}

      {showControlButtons && (
        <div className="image-gallery-headline__controls">
          <ImageGalleryControlButtons
            variant={isVideoPlaying ? 'opaque' : 'transparent'}
            onNextHeadline={onNextHeadline}
            onPreviousHeadLine={onPreviousHeadLine}
            onMoveOver={handleMouseOverButton}
            isConnect={isConnect}
          />
        </div>
      )}

      <div className={cx('image-gallery-headline__media', isVideoContent && 'image-gallery-headline__media--video')}>
        {renderMediaComponent()}
      </div>

      {shouldShowZoom && (
        <div
          className="image-gallery-headline__zoom"
          style={{
            backgroundImage: `url(${activeImgZoomBaseSrc})`,
            backgroundSize: `${zoomSize.x || (headlineRef.current ? headlineRef.current.clientWidth * 2 : 800)}px ${
              zoomSize.y || (headlineRef.current ? headlineRef.current.clientHeight * 2 : 800)
            }px`,
            backgroundPosition: `${zoomLevel.x}px ${zoomLevel.y}px`,
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      <style jsx>{ImageGalleryHeadlineDesktopStyles}</style>
    </div>
  );
};

ImageGalleryHeadlineDesktop.defaultProps = {
  showControlButtons: true,
  crop: undefined,
  setVideoPlayingForSoftline: noop,
  isConnect: false,
  images: [],
};

ImageGalleryHeadlineDesktop.propTypes = {
  zoomSize: PropTypes.object.isRequired,
  activeImageIndex: PropTypes.number.isRequired,
  images: PropTypes.array,
  imagesPerSlide: PropTypes.number.isRequired,
  activeIndicatorIndex: PropTypes.number.isRequired,
  setActiveIndicatorIndex: PropTypes.func.isRequired,
  setActiveImageIndex: PropTypes.func.isRequired,
  imageQuality: PropTypes.number.isRequired,
  headlineImageHeight: PropTypes.number.isRequired,
  headlineImageWidth: PropTypes.number.isRequired,
  showControlButtons: PropTypes.bool,
  crop: PropTypes.object,
  setVideoPlayingForSoftline: PropTypes.func,
  isConnect: PropTypes.bool,
  productName: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
};

export default withApplicationContext(withProduct(ImageGalleryHeadlineDesktop));
export { ImageGalleryHeadlineDesktop };
